import Big from 'big.js';
import type { SizeValue } from '../components/BlotterTable/aggFuncs/types';
import type { Currency } from '../types/Currency';
import { toBig } from './number';

export type AvailableMarginRow = {
  AvailableMarginToBuy?: string;
  AvailableMarginToSell?: string;
  MarginCurrency?: string;
};

/**
 * Available margin = min(AvailableMarginToBuy, AvailableMarginToSell).
 * Returns empty if either side is missing or not parseable — both must be present.
 */
export function getAvailableMargin(data: AvailableMarginRow | undefined): string {
  if (!data) {
    return '';
  }
  const buy = toBig(data.AvailableMarginToBuy);
  const sell = toBig(data.AvailableMarginToSell);
  if (buy == null || sell == null) {
    return '';
  }
  return buy.lte(sell) ? buy.toFixed() : sell.toFixed();
}

/**
 * Size column value for AG Grid: numeric string plus display currency and increment from context.
 * Matches size column behavior for `currencyField: 'MarginCurrency'`.
 */
export function getAvailableMarginSizeValue(
  data: AvailableMarginRow | undefined,
  context: { current: { currenciesBySymbol?: Map<string, Currency> } }
): SizeValue {
  const value = getAvailableMargin(data);
  const currencyKey = data?.MarginCurrency ?? '';
  const currencyInfo = currencyKey ? context.current.currenciesBySymbol?.get(currencyKey) : undefined;
  return {
    value,
    currency: currencyInfo?.Symbol ?? currencyKey,
    increment: currencyInfo?.DefaultIncrement,
  };
}

export type TradingStatusRow = AvailableMarginRow & {
  Amount?: string;
  Equity?: string;
  Collateral?: string;
  MarginCallThreshold?: string;
};

export function getTradingStatus(data?: TradingStatusRow): string {
  if (!data) {
    return '';
  }

  const equity = data.Equity ? Big(data.Equity) : null;
  const collateral = data.Collateral ? Big(data.Collateral) : null;
  const marginCallThreshold = data.MarginCallThreshold ? Big(data.MarginCallThreshold) : null;
  const amount = data.Amount ? Big(data.Amount) : null;
  const availableBuyMargin = data.AvailableMarginToBuy ? Big(data.AvailableMarginToBuy) : null;
  const availableSellMargin = data.AvailableMarginToSell ? Big(data.AvailableMarginToSell) : null;

  // 1. Check: Equity < MarginCallThreshold * Collateral
  if (equity && collateral && marginCallThreshold) {
    const threshold = marginCallThreshold.times(collateral);
    if (equity.lt(threshold)) {
      return 'Margin Call';
    }
  }

  // 2. Check: Margin Call when AvailableMargin = 0 based on position side
  if (amount) {
    if (amount.gt(0) && availableBuyMargin && availableBuyMargin.eq(0)) {
      return 'Margin Call';
    }
    if (amount.lt(0) && availableSellMargin && availableSellMargin.eq(0)) {
      return 'Margin Call';
    }
  }

  return 'Trading'; // Default case
}
